// eslint-disable-next-line
import * as Sentry from "@sentry/react";
import posthog from "posthog-js";

import {
  LOGIN_USER_REQUEST,
  LOGIN_USER_FAILURE,
  LOGIN_USER_SUCCESS,
  LOGOUT_USER,
  UPDATE_PROFILE,
  UPDATE_EMAIL_VERIFICATION,
  SHOW_LOGIN_MODAL,
  REDIRECT_AFTER_LOGIN,
  SET_IS_PLATFORM_INVITE_LOADING,
  SET_PLATFORM_INVITE,
  UPDATE_PROFILE_KEYS,
  UPDATE_GAMEPLAYER_PROFILE_KEYS,
  SHOW_OAUTH_REGISTER_MODAL,
  CONTACT_INFO,
  AUTH_PROVIDER,
  UPDATE_TAG_LINE,
  SHOW_SHORTLIST_MODAL,
  SET_CRM_PROFILE,
} from "./types";
import activity from "../../common/activity/activityLogger";
import store from "../../../store";
import * as UserListActions from "../../profile/common/components/UserList/duck/actions";
import {
  ACCESS_TOKEN_TIME,
  GTM_EVENT_LOGIN_MODAL_OPENED,
  REFRESH_TOKEN_TIME,
} from "./constants";
import { ChatWootUtils } from "../../common/chatwoot";
import axios from "../../common/axios";
import { API_URL } from "../../common/constants";

function checkIfCookieExists(cookieName) {
  // Split document.cookie by semicolons into an array
  let cookies = document.cookie.split(";");

  // Loop through the array elements
  for (let i = 0; i < cookies.length; i++) {
    // Trim the cookie name and value
    let name = cookies[i].substring(0, cookies[i].indexOf("=")).trim();

    // If the cookie is found, return true
    if (name === cookieName) {
      return true;
    }
  }

  // If the cookie is not found, return false
  return false;
}

function loginUserSuccess(
  accessToken,
  refreshToken,
  userProfile,
  isAccountReactivated = false
) {
  store.dispatch(UserListActions.clearUserListState());
  localStorage.setItem("accessToken", accessToken);
  localStorage.setItem("refreshToken", refreshToken);
  localStorage.setItem("user", JSON.stringify(userProfile));

  if (isAccountReactivated) {
    window.alert("You have reactivated your account.");
  }

  var now = new Date();
  var time = now.getTime();
  var expireTime = time + 1000 * 360000;
  now.setTime(expireTime);
  document.cookie = `username=${
    userProfile?.userName
  }; expires=${now.toUTCString()};path=/`;
  window?.google?.accounts?.id?.cancel();
  // Hide the One-Tap container so even if the prompt renders after cancel(), it stays invisible
  const oneTapContainer = document.getElementById("g_id_onload");
  if (oneTapContainer) {
    oneTapContainer.style.display = "none";
  }

  // Store user JWT tokens in cookies
  const accessTokenTime = new Date(Date.now() + ACCESS_TOKEN_TIME);
  document.cookie = `accessToken=${accessToken}; expires=${accessTokenTime.toUTCString()}; path=/; domain=${
    window.location.host
  };`;
  // Only update refresh token if it not already exists
  if (!checkIfCookieExists("refreshToken")) {
    const refreshTokenTime = new Date(Date.now() + REFRESH_TOKEN_TIME);
    document.cookie = `refreshToken=${refreshToken}; expires=${refreshTokenTime.toUTCString()}; path=/; domain=${
      window.location.host
    }`;
  }

  ChatWootUtils.syncUserMetaData(userProfile);

  // Logging user profile data
  activity.logUserProfileData({
    state: "REGISTERED_USER",
    gamePlayer: userProfile?._id,
    firstName: userProfile?.firstName,
    lastName: userProfile?.lastName,
    email: userProfile?.email,
  });

  return {
    type: LOGIN_USER_SUCCESS,
    payload: {
      accessToken: accessToken,
      profile: userProfile,
    },
  };
}

function loginUserFailure(error) {
  localStorage.removeItem("accessToken");
  localStorage.removeItem("refreshToken");
  localStorage.removeItem("user");
  return {
    type: LOGIN_USER_FAILURE,
    payload: {
      authenticationStatusText: error.response.message,
    },
  };
}

function loginUserRequest(provider) {
  return {
    type: LOGIN_USER_REQUEST,
    payload: {
      provider: provider,
    },
  };
}

function logout() {
  navigator?.serviceWorker?.getRegistrations().then((r) => {
    return Promise.all(r.map((reg) => reg.unregister()));
  });

  const instanceId = localStorage.getItem("instanceId");
  const token = localStorage.getItem("fcmToken");
  /* permanently delete tokens if found in local storage */
  if (instanceId && token) {
    axios
      .post(`${API_URL}/v1/auth/logout`, { instanceId, token })
      .catch(Sentry.captureException);
  }
  store.dispatch(UserListActions.clearUserListState());
  localStorage.removeItem("accessToken");
  localStorage.removeItem("refreshToken");
  localStorage.removeItem("user");
  localStorage.removeItem("fcmToken");
  localStorage.removeItem("instanceId");
  localStorage.removeItem("showNotificationModal");
  localStorage.removeItem("lastShortlistPrompt");
  document.cookie = `username=; expires=Thu, 01 Jan 1970 00:00:00 UTC; path=/;`;

  // Remove JWT tokens from cookies
  document.cookie =
    "accessToken=; expires=Thu, 01 Jan 1970 00:00:00 UTC; path=/;";
  document.cookie =
    "refreshToken=; expires=Thu, 01 Jan 1970 00:00:00 UTC; path=/;";

  window.$chatwoot?.reset();

  posthog.reset();

  // Logging user profile data
  activity.logUserProfileData({
    state: "ANONYMOUS_PROFILE",
  });

  return {
    type: LOGOUT_USER,
  };
}

function updateProfile(userProfile) {
  return {
    type: UPDATE_PROFILE,
    payload: {
      profile: userProfile,
    },
  };
}

function updateUserEmailVerification(isEmailVerified) {
  return {
    type: UPDATE_EMAIL_VERIFICATION,
    payload: {
      isEmailVerified: isEmailVerified,
    },
  };
}

function isOpenShortListModal(value) {
  return {
    type: SHOW_SHORTLIST_MODAL,
    payload: { value },
  };
}

function showLoginModal(
  shouldShow = false,
  {
    redirectTo,
    loginPrompt,
    isLoginDismissible,
    onModalHideCallback,
    preferredLoginMethod,
  } = {
    redirectTo: null,
    loginPrompt: null,
    isLoginDismissible: false,
    onModalHideCallback: null,
    preferredLoginMethod: null,
  }
) {
  if (shouldShow) {
    window.dataLayer?.push({ event: GTM_EVENT_LOGIN_MODAL_OPENED });
  }
  return {
    type: SHOW_LOGIN_MODAL,
    payload: {
      value: shouldShow,
      redirectTo: redirectTo,
      loginPrompt: loginPrompt,
      isLoginDismissible: isLoginDismissible,
      onModalHideCallback,
      preferredLoginMethod,
    },
  };
}

function redirectAfterLogin(value) {
  return {
    type: REDIRECT_AFTER_LOGIN,
    payload: { value },
  };
}

function setIsPlatformInviteLoading(value) {
  return {
    type: SET_IS_PLATFORM_INVITE_LOADING,
    payload: { value },
  };
}

function setPlatformInvite(value) {
  return {
    type: SET_PLATFORM_INVITE,
    payload: { value },
  };
}

function updateProfileKeys(payload) {
  return {
    type: UPDATE_PROFILE_KEYS,
    payload,
  };
}

function updateGamePlayerProfileKeys(payload) {
  return {
    type: UPDATE_GAMEPLAYER_PROFILE_KEYS,
    payload,
  };
}

function showOAuthRegisterModal(value) {
  return {
    type: SHOW_OAUTH_REGISTER_MODAL,
    payload: { value },
  };
}

function setContactInfo(value) {
  return {
    type: CONTACT_INFO,
    payload: { value },
  };
}

function authProvider(value) {
  return {
    type: AUTH_PROVIDER,
    payload: { value },
  };
}

function updateTagLine(data) {
  return { type: UPDATE_TAG_LINE, payload: { tagLine: data } };
}

function setCrmProfile(crmProfile) {
  return { type: SET_CRM_PROFILE, payload: { crmProfile } };
}

const Actions = {
  loginUserSuccess,
  loginUserRequest,
  loginUserFailure,
  logout,
  updateProfile,
  updateUserEmailVerification,
  showLoginModal,
  redirectAfterLogin,
  setIsPlatformInviteLoading,
  setPlatformInvite,
  updateProfileKeys,
  updateGamePlayerProfileKeys,
  showOAuthRegisterModal,
  setContactInfo,
  authProvider,
  updateTagLine,
  isOpenShortListModal,
  setCrmProfile,
};

export default Actions;
